// Libraries
import { action, computed, observable, runInAction } from "mobx";

// Core
import { ViewModelBase } from "Core/ViewModels/ViewModelBase";
import { FieldType, isNullOrEmpty } from "Core/Utils/Utils";

// Custom
import { Server } from "Custom/Globals/AppUrls";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { ReservationAndRelatedModelDTO, ReservationAndRelatedModel } from "Custom/Models/DashboardModels/ReservationAndRelatedModel";
import { ApiResult } from "Core/Models/ApiResult";
import { SignatureRequestStatusType, SignatureStatusType } from "Custom/Models/Enums/SignatureStatusType";
import { StoresInstance } from "Custom/Stores";

export class SignaturesSectionViewModel extends ViewModelBase<ReservationAndRelatedModel> {
    // #region Properties

    @observable
    public apiStatus: ApiStatus = ApiStatus.loading;

    @observable
    public propertyId: string = "";

    @observable
    public loggedInUserSignatureRole: string = "";

    // #endregion

    // #region Constructor

    constructor(propertyId: string) {
        super(new ReservationAndRelatedModel());
        this.setDecorators(ReservationAndRelatedModel);
        this.propertyId = propertyId;
        this.loggedInUserSignatureRole = StoresInstance.domain.UserStore ? StoresInstance.domain.UserStore.userSignatureRole : "";
        this.loadReservationData();
    }

    // #endregion

    // #region Computed Properties

    @computed
    public get ApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    @computed
    get isPurchaserOneLoggedIn(): boolean {
        if (this.loggedInUserSignatureRole == "purchaserTwo") return false;
        return true;
    }

    @computed
    public get purchaserOneSignatureStatus(): SignatureStatusType {
        const status = this.model?.signatureStatus;
        if (!status) return SignatureStatusType.Pending;

        return status.purchaserOneStatus;
    }

    @computed
    public get purchaserTwoSignatureStatus(): SignatureStatusType {
        const status = this.model?.signatureStatus;
        if (!status) return SignatureStatusType.Pending;

        return status.purchaserTwoStatus;
    }

    @computed
    public get isSignatureComplete(): boolean {
        if (this.isPurchaserOneLoggedIn) {
            return this.purchaserOneSignatureStatus === SignatureStatusType.Complete;
        } else {
            return this.purchaserTwoSignatureStatus === SignatureStatusType.Complete;
        }
    }

    @computed
    public get hasOutstandingSignatures(): boolean {
        const status = this.model?.signatureStatus;
        if (!status) return false;

        const purchaserOneRequired = !isNullOrEmpty(status.purchaserOneToken) && status.purchaserOneToken !== "";
        const purchaserTwoRequired = !isNullOrEmpty(status.purchaserTwoToken) && status.purchaserTwoToken !== "";

        const purchaserOneOutstanding = purchaserOneRequired && status.purchaserOneStatus !== SignatureStatusType.Complete;
        const purchaserTwoOutstanding = purchaserTwoRequired && status.purchaserTwoStatus !== SignatureStatusType.Complete;

        const isExpired = status && new Date(status.expiryDate) < new Date();
        const cardAvailable = status && status.requestStatus === SignatureRequestStatusType.Published && !isExpired && !this.isSignatureComplete;
        
        return purchaserOneOutstanding && purchaserTwoOutstanding && cardAvailable;
    }

    // #endregion

    // #region Actions

    @action
    public setApiStatus(apiStatus: ApiStatus) {
        this.apiStatus = apiStatus;
    }

    @action
    public async loadReservationData(): Promise<void> {
        try {
            this.setApiStatus(ApiStatus.loading);

            const apiResult = await this.Get<ReservationAndRelatedModelDTO>(Server.Api.ReservationType1.GetReservationType1AndRelated + "/" + this.propertyId);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (error) {
            console.error("Failed to load reservation data:", error);
            this.setApiStatus(ApiStatus.error);
        }
    }

    // #endregion

    // #region Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<any>): boolean {
        return true;
    }

    // #endregion
}
